import { ButtonSubmit } from '../../../Framework/Components/Buttons/Button';
import { ColourMod } from '../../../Framework/Data/Enums/LayoutEnums';
import React, { useState } from 'react';
import Modal from '../../../Framework/Components/Modals/Modal';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { UploadFile } from '../../../Framework/Components/Forms/File/UploadFile';
import AddressImporterForm from '../Components/AddressImporterForm';
import { AddressImporterModalType } from '../AddressImportTypes';
import { ModalSize } from '../../../Framework/Data/Enums/Generic';
import AlertBanner from '../../../Framework/Components/AlertBanner/AlertBanner';
import { maxCloudflareFileSizeInBytes } from '../../../Api/SendMailEndpoints';

enum HeaderNameMap {
	Name = 'name',
	BusinessName = 'businessName',
	PropertyNumber = 'propertyName',
	Street = 'addressLine1',
	City = 'addressLine2',
	Locality = 'addressLine3',
	PostCode = 'postCode',
	CountryName = 'countryName',
	CountryCode = 'countryCode',
	EmailAddress = 'emailAddress',
	PhoneNumber = 'phoneNo',
}

const ImportCsvModal = (props: AddressImporterModalType) => {
	const [importing, setImporting] = useState<boolean>(false);
	const [parsedData, setParsedData] = useState<any>(null);

	const [parseError, setParseError] = useState<any>({ state: false, title: '', message: '' });

	const { handleSubmit, getValues, control, formState } = useForm({
		defaultValues: {
			file: null,
		},
	});

	const MapHeaderValues = (row: string) => {
		const data: Array<string> = SanitiseRow(row);

		for (let i = 0; i < data.length; i++) {
			// @ts-ignore
			data[i] = HeaderNameMap[data[i]];
		}

		return data;
	};

	const SanitiseRow = (row: string): Array<string> => {
		const values = row.split(',');

		for (let i = 0; i < values.length; i++) {
			values[i] = values[i].replace(/"/g, '').trim();
		}

		return values;
	};

	const ParseCsv = (input: any) => {
		const data = input.replace(/\n\r/, '\n').trim().split('\n');

		const requiredHeadersLength = Object.keys(HeaderNameMap).length;

		const headers = MapHeaderValues(data[0]);
		const MissingHeaderNames: Array<string> = Array.from(Object.keys(HeaderNameMap)).filter(
			(header) => data[0].indexOf(header) === -1
		);

		data.splice(0, 1);

		const MappedData: any = [];

		data.forEach((line: any) => {
			const lineValues = SanitiseRow(line.trim());
			const emptyValues = [];

			for (let i = 0; i < lineValues.length; i++) {
				emptyValues[i] = lineValues[i] === '';
			}

			if (lineValues.length === requiredHeadersLength && !emptyValues.every((item: boolean) => item)) {
				let address: Record<any, any> = {};

				for (let i = 0; i < lineValues.length; i++) {
					address[headers[i]] = lineValues[i]?.trim();
				}

				MappedData.push(address);
			}
		});

		setImporting(false);

		if (MappedData.length > 0) {
			setParseError({ state: false, title: '', message: '' });
			if (props.forwardData && props.actionedCallback) {
				props.actionedCallback(MappedData);
			} else {
				setParsedData(MappedData);
			}
		} else {
			if (headers.length !== requiredHeadersLength) {
				setParseError({
					state: true,
					title: 'Missing Columns',
					message:
						MissingHeaderNames.length === 1
							? `The selected file is missing column: '${MissingHeaderNames.map((header) => header)}'`
							: `The selected file is missing columns: '${MissingHeaderNames.map((header) => header)}'`,
				});
			} else {
				setParseError({
					state: true,
					title: 'No Data',
					message: 'The selected file contains no readable data',
				});
			}
		}
	};

	const ReadLocalFile = (data: any) => {
		const reader = new FileReader();

		reader.onload = () => {
			ParseCsv(reader.result);
		};

		reader.readAsBinaryString(data.file[0]);
	};

	const HandleImport = (data: FieldValues, e: any) => {
		setImporting(true);
		ReadLocalFile(data);
	};

	const HandleSave = (data: any) => {
		if (props.actionedCallback) {
			props.actionedCallback(data);
		}
	};

	return (
		<Modal
			title={'Import CSV File'}
			isOpen={props.visible}
			closeCallback={() => props.closeCallback()}
			modalSize={parsedData ? ModalSize.ExtraLarge : ModalSize.Default}
		>
			{!parsedData && (
				<form onSubmit={handleSubmit((data, e) => HandleImport(data, e))}>
					<fieldset className={'flex-column flex-gap-md'}>
						<AlertBanner
							title={'CSV Data Structure'}
							content={'Please use our CSV address template to ensure your address(es) can be imported'}
							addOn={
								<a
									className={'btn primary'}
									download
									href={`${process.env.PUBLIC_URL}/UKPostboxAddressImportTemplate.csv`}
								>
									Download CSV
								</a>
							}
						/>

						<Controller
							control={control}
							name='file'
							rules={{ required: 'A file is required' }}
							render={({ field }) => (
								<UploadFile
									name={'file csv'}
									onChange={(e: any) => field.onChange(e)}
									onError={(errorState) => console.log(errorState)}
									acceptsFileExtension={['.csv']}
									extensionDisplayValue={'CSV'}
									singleFileOnly={true}
									componentError={parseError}
									maxSizeInBytes={maxCloudflareFileSizeInBytes}
									maxFiles={1}
								/>
							)}
						/>

						<ButtonSubmit
							loading={importing}
							disabled={importing || !formState.isValid}
							title={'Import'}
							fullWidth={true}
							colour={ColourMod.Primary}
						/>
					</fieldset>
				</form>
			)}

			{parsedData && (
				<AddressImporterForm
					addressData={parsedData}
					embeddedForm={true}
					savedCallback={(data: any) => HandleSave(data)}
					removeCallback={() => setParsedData(null)}
				/>
			)}
		</Modal>
	);
};

export default ImportCsvModal;
